import React, { useState, useEffect, memo } from 'react';
import { Form, Input, Select, Upload, Card, Spin, message, Row, Col, Image, Checkbox } from 'antd';
import { Button } from 'components';
import {
  SaveOutlined,
  ArrowLeftOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useHomepageBlocks from '../useHomepageBlocks';
import BasicEditor from 'components/RichTextEditor/BasicEditor';
import ErrorHelper from 'helpers/ErrorHelper';

const { Option } = Select;

export const HomepageBlockForm = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [initialData, setInitialData] = useState(null);
  const [selectedShoppingCenter, setSelectedShoppingCenter] = useState(null);
  const {
    shoppingCenters,
    loadingShoppingCenters,
    createHomepageBlock,
    updateHomepageBlock,
    fetchHomepageBlock,
  } = useHomepageBlocks();

  const isEditMode = !!id;

  // Function to determine available languages based on Shopping Center
  const getAvailableLanguages = () => {
    if (!selectedShoppingCenter) return ['IT', 'EN'];

    const isTorino =
      selectedShoppingCenter.title?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.name?.toLowerCase().includes('torino') ||
      selectedShoppingCenter.id === 'torino';

    return isTorino ? ['IT', 'EN', 'FR'] : ['IT', 'EN'];
  };

  const availableLanguages = getAvailableLanguages();

  // Load homepage block data for editing
  useEffect(() => {
    if (isEditMode) {
      const loadHomepageBlock = async () => {
        setLoading(true);
        try {
          const homepageBlock = await fetchHomepageBlock(id);
          if (homepageBlock) {
            setInitialData(homepageBlock);
            setSelectedShoppingCenter(homepageBlock.shopping_center);
            
            // Set form values
            form.setFieldsValue({
              title_it: homepageBlock.title_it,
              title_en: homepageBlock.title_en,
              title_fr: homepageBlock.title_fr,
              description_it: homepageBlock.description_it,
              description_en: homepageBlock.description_en,
              description_fr: homepageBlock.description_fr,
              link_it: homepageBlock.link_it,
              link_en: homepageBlock.link_en,
              link_fr: homepageBlock.link_fr,
              shopping_center_id: homepageBlock.shopping_center?.id,
              show_link: homepageBlock.show_link,
            });

            // Set file if exists
            if (homepageBlock.file?.url) {
              setFileList([{
                uid: '-1',
                name: homepageBlock.file.file_name,
                status: 'done',
                url: homepageBlock.file.url,
              }]);
            }
          }
        } catch (error) {
          console.error('Error loading homepage block:', error);
          message.error('Failed to load homepage block');
        } finally {
          setLoading(false);
        }
      };

      loadHomepageBlock();
    }
  }, [id, isEditMode, fetchHomepageBlock, form]);

  const handleSubmit = async (values) => {
    setSubmitting(true);

    try {
      const formData = {
        title_it: values.title_it,
        title_en: values.title_en,
        title_fr: values.title_fr,
        description_it: values.description_it,
        description_en: values.description_en,
        description_fr: values.description_fr,
        link_it: values.link_it,
        link_en: values.link_en,
        link_fr: values.link_fr,
        shopping_center_id: values.shopping_center_id,
        show_link: values.show_link,
      };

      // Add file if uploaded (new file or existing file in edit mode)
      if (fileList.length > 0) {
        if (fileList[0].originFileObj) {
          formData.file = fileList[0].originFileObj;
        }
      }

      const result = isEditMode
        ? await updateHomepageBlock(id, formData)
        : await createHomepageBlock(formData);

      if (result) {
        navigate('/website/homepage_blocks');
      }
    } catch (error) {
      console.error('Error saving homepage block:', error);
      ErrorHelper.applyToForm(form, error, t('Failed to save homepage block'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };


  const uploadProps = {
    name: 'file',
    multiple: false,
    fileList,
    onChange: handleFileChange,
    beforeUpload: () => false, // Prevent auto upload
    accept: 'image/*',
  };

  if (loading && isEditMode) {
    return (
      <div style={{ textAlign: 'center', padding: '50px' }}>
        <Spin size='large' />
      </div>
    );
  }

  return (
    <div>
      <Card
        title={isEditMode ? t('Modifica Homepage Block') : t('Crea Nuovo Homepage Block')}
        extra={
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/website/homepage_blocks')}
          >
            {t('Indietro')}
          </Button>
        }
      >
        <Form
          form={form}
          layout='vertical'
          onFinish={handleSubmit}
          scrollToFirstError
          initialValues={{
            shopping_center_id: initialData?.shopping_center?.id,
          }}
        >
          <Form.Item
            name='shopping_center_id'
            label={t('Outlet Village')}
            rules={[
              { required: true, message: t('Seleziona un Outlet Village') },
            ]}
          >
            <Select
              placeholder={t('Seleziona Outlet Village')}
              loading={loadingShoppingCenters}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              onChange={(value) => {
                const center = shoppingCenters.find(
                  (c) => (c.value || c.id) === value,
                );
                setSelectedShoppingCenter(center);
              }}
            >
              {shoppingCenters
                .filter(
                  (center) =>
                    center &&
                    (center.value || center.id) &&
                    (center.title || center.name),
                )
                .map((center) => (
                  <Option
                    key={center.value || center.id}
                    value={center.value || center.id}
                  >
                    {center.title || center.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          <div style={{ marginBottom: '24px' }}>

            {/* Titles Row */}
            <Row gutter={16}>
              <Col span={availableLanguages.length === 3 ? 8 : 12}>
                <Form.Item name='title_it' label={`${t('Titolo')} IT`}>
                  <Input
                    placeholder={t('Inserisci il titolo')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={availableLanguages.length === 3 ? 8 : 12}>
                <Form.Item name='title_en' label={`${t('Titolo')} EN`}>
                  <Input
                    placeholder={t('Inserisci il titolo')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              {availableLanguages.includes('FR') && (
                <Col span={8}>
                  <Form.Item name='title_fr' label={`${t('Titolo')} FR`}>
                    <Input
                      placeholder={t('Inserisci il titolo')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>

            {/* Descriptions*/}
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  name='description_it'
                  label={`${t('Descrizione')} IT`}
                >
                  <BasicEditor
                    rows={4}
                    placeholder={t('Inserisci la descrizione')}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item name='description_en' label={`${t('Descrizione')} EN`}>
                  <BasicEditor
                    rows={4}
                    placeholder={t('Inserisci la descrizione')}
                  />
                </Form.Item>
              </Col>
            </Row>
            
            {availableLanguages.includes('FR') && (
              <Row gutter={16}>
                <Col span={24}>
                  <Form.Item name='description_fr' label={`${t('Descrizione')} FR`}>
                    <BasicEditor
                      rows={4}
                      placeholder={t('Inserisci la descrizione')}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {/* Links Row */}
            <Row gutter={16}>
              <Col span={availableLanguages.length === 3 ? 8 : 12}>
                <Form.Item name='link_it' label={`${t('Link')} IT`}>
                  <Input
                    placeholder={t('Inserisci il link')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col span={availableLanguages.length === 3 ? 8 : 12}>
                <Form.Item 
                  name='link_en' 
                  label={`${t('Link')} EN`}
                >
                  <Input
                    placeholder={t('Inserisci il link')}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              {availableLanguages.includes('FR') && (
                <Col span={8}>
                  <Form.Item 
                    name='link_fr' 
                    label={`${t('Link')} FR`}
                  >
                    <Input
                      placeholder={t('Inserisci il link')}
                      style={{ width: '100%' }}
                    />
                  </Form.Item>
                </Col>
              )}
              <Col span={24}>
                <Form.Item name='show_link' label={t('Mostra il link')}  valuePropName='checked'>
                  <Checkbox />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name='file'
                label={t('Immagine')}
                rules={[
                  {
                    validator: (_, value) => {
                      // If editing and fileList has existing file, it's valid
                      if (isEditMode && fileList.length > 0 && fileList[0].url) {
                        return Promise.resolve();
                      }
                      // If creating or no existing file, require new file
                      if (fileList.length === 0 || !fileList[0].originFileObj) {
                        return Promise.reject(new Error(t('Carica un immagine')));
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Upload.Dragger {...uploadProps}>
                  <p className='ant-upload-drag-icon'>
                    <InboxOutlined />
                  </p>
                  <p className='ant-upload-text'>{t('Clicca o trascina un file qui per caricarlo')}</p>
                  <p className='ant-upload-hint'>
                    {t('Supporta caricamento singolo. Accetta solo immagini')}
                  </p>
                </Upload.Dragger>
              </Form.Item>
            </Col>
          </Row>

          {fileList.length > 0 && fileList[0].url && (
            <Row gutter={16}>
              <Col span={24}>
                <div style={{ marginBottom: 16 }}>
                  <label>{t('Anteprima immagine')}:</label>
                  <div style={{ marginTop: 8 }}>
                    <Image
                      src={fileList[0].url}
                      alt='Preview'
                      style={{ maxWidth: 200, maxHeight: 200 }}
                    />
                  </div>
                </div>
              </Col>
            </Row>
          )}
          
        </Form>
        
        
        <div style={{ marginTop: 24, textAlign: 'right' }}>
          <Button
            type='primary'
            icon={<SaveOutlined />}
            onClick={() => form.submit()}
            loading={submitting}
            size='large'
          >
            {t('Salva')}
          </Button>
        </div>
      </Card>
    </div>
  );
});

export default HomepageBlockForm;
